/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 사용자 네비게이션 바
 * 버전 : 1.0
 * 파일명 : NavBar.tsx
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * 수정 : 2025-07-02, 강동형 : 저작권 추가, 페이지 추가에 따른 헤더 메세지 추가
 * ================================================================ 
 * 수정 : 2025-07-04, 강동형 : 홍보 대상 추가 페이지 추가에 따른 헤더 메세지 추가
 * 수정 : 2025-07-08, 강동형 : 홍보 대상 추가 페이지 추가에 따른 헤더 메세지 삭제
 * 수정 : 2025-12-18, 강동형 : 헤더 사이즈 획일화
 * ================================================================ */

import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import NavItem from './NavItem';
import { IconButton, Divider as MuiDivider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../../assets/images/Army_Link.svg';
import hamburger from '../../assets/images/hamburger.svg';
import { useStorageStore } from 'src/stores/StorageStore';
import { postLogout } from 'src/api/Auth';
import Info from '../schoolSearch/Info';
import { useBackHandlerStore, useNavbarStore } from 'src/stores/UIStore';
import { Icon_ArrowBack, Icon_Inform_White } from './CommonIcons';

// 네비게이션 바 스타일 정의
const NavBarContainer = styled.div<{ $isHome: boolean }>`
  display: flex; // Flexbox 레이아웃을 사용하여 자식 요소들을 수평으로 배치합니다.
  justify-content: space-between; // 자식 요소들 사이의 간격을 동일하게 유지합니다.
  align-items: center; // 자식 요소들을 수직 가운데 정렬합니다.

  min-width: 100vw; // 최대 너비를 화면 너비로 설정합니다.
  padding: ${({ $isHome }) => ($isHome ? "5px 18px" : "10px 20px")};
  background-color: white; // 네비게이션 바의 배경색을 흰색으로 설정합니다.
  z-index: 400; // 다른 요소들 위에 표시되도록 z-index 설정
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); // 그림자 추가
  overflow: hidden; // 내용이 넘칠 경우 숨깁니다.
  max-height: 56px;
  position: fixed;
  top: 0;
`;

// 로고 스타일 정의
const NavBarLogo = styled.div`
  flex: 1; // 로고가 다른 아이템보다 더 큰 비율을 가지도록 설정합니다.
  display: flex; // Flexbox 레이아웃을 사용하여 자식 요소들을 수평으로 배치합니다.
  justify-content: center; // 로고를 수평 가운데 정렬합니다.
  align-items: center; // 로고를 수직 가운데 정렬합니다.
`;

// 사이드 메뉴 스타일 정의
const SideMenu = styled.div<{ $isopen: boolean }>`
  position: fixed; // 사이드 메뉴 바를 고정 위치에 배치합니다.
  top: 0; // 사이드 메뉴 바의 상단 위치를 0으로 설정합니다.
  right: ${(props) => (props.$isopen ? '0%' : '-100%')}; // 사이드바가 열리면 0, 닫히면 -100%에 위치합니다.
  width: 65%; 
  height: 100%; // 사이드 메뉴 바의 높이를 100%로 설정합니다.
  background-color: white; // 사이드 메뉴 바의 배경색을 흰색으로 설정합니다.
  transition: right 0.3s ease; // 위치 변경 시 애니메이션 효과를 추가합니다.
  z-index: 1302; // 사이드 메뉴 바의 z-index를 설정하여 다른 요소 위에 나타나도록 합니다.
  padding: 1rem; // 사이드 메뉴 바의 패딩을 1rem로 설정합니다.

  ul {
    list-style: none; // 리스트 아이템의 기본 스타일을 제거합니다.
    padding: 0; // 리스트 아이템의 패딩을 제거합니다.
  }

  li {
    margin: 1rem 0; // 리스트 아이템의 간격을 설정합니다.
  }

  a {
    text-decoration: none; // 링크의 밑줄을 제거합니다.
    color: black; // 링크의 색상을 검정색으로 설정합니다.
    font-size: 1.2rem; // 링크의 글꼴 크기를 설정합니다.
  }
`;

// 오버레이 스타일 정의
const Overlay = styled.div<{ $isopen: boolean }>`
  display: ${(props) => (props.$isopen ? 'block' : 'none')}; // 오버레이의 표시 여부를 설정합니다.
  position: fixed; // 오버레이를 고정 위치에 배치합니다.
  top: 0; // 오버레이의 상단 위치를 0으로 설정합니다.
  left: 0; // 오버레이의 왼쪽 위치를 0으로 설정합니다.
  width: 100%; // 오버레이의 너비를 100%로 설정합니다.
  height: 100%; // 오버레이의 높이를 100%로 설정합니다.
  background-color: rgba(0, 0, 0, 0.5); // 오버레이의 배경색을 반투명 검정색으로 설정합니다.
  z-index: 1301; // 오버레이의 z-index를 설정하여 사이드 메뉴 바 아래에 나타나도록 합니다.
`;

// 사이드 메뉴 헤더 스타일 정의
const SideMenuHeader = styled.div`
  display: flex; // Flexbox 레이아웃을 사용하여 자식 요소들을 수평으로 배치합니다.
  justify-content: flex-end; // 자식 요소들을 우측 정렬합니다.
  align-items: center; // 자식 요소들을 수직 가운데 정렬합니다.
  margin-bottom: 2rem; // 아래쪽 마진을 설정합니다.
`;

// 사용자 정보 스타일 정의
const UserInfo = styled.div`
  text-align: left; // 텍스트를 왼쪽으로 정렬합니다.
  margin-bottom: 2rem; // 아래쪽 마진을 설정합니다.
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  .greeting {
    color: grey; // 인사말의 색상을 회색으로 설정합니다.
    margin-bottom: 0.5rem; // 아래쪽 마진을 설정합니다.
  }

  .username {
    font-size: 1.5rem; // 사용자 이름의 글꼴 크기를 설정합니다.
    font-weight: bold; // 사용자 이름의 글꼴을 두껍게 설정합니다.
    margin-bottom: 1rem; // 아래쪽 마진을 설정합니다.
  }
`;

const LogoutBox = styled.div`
  margin-top: auto;
  text-decoration: underline;
`

// 구분선 스타일 정의
const StyledDivider = styled(MuiDivider)`
  margin: 1rem 0; // 위아래 마진을 설정합니다.
`;

const LogoImg = styled.img`
    max-height: 38px;
    /* width: auto; */
    /* box-sizing: border-box; */
    /* margin: 4px; */
    display: block;
`

// Styled component for the logo text
const LogoText = styled.span`
  font-weight: bold;
  font-size: 18px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
`
const AIRecommendationWrapper = styled.div`
  border-radius: 100px;
  background: linear-gradient(180deg, #47D5FF 0%, #04A7D7 100%);
  font-size: 12px;
  color: white;
  padding: 3.5px 10px;
  font-weight: 500;
`

const NavBar = () => {
  const location = useLocation();
  const store = useStorageStore()
  const navigate = useNavigate();
  const [isInfo,setIsInfo] = useState(false)
  const isvisible = useNavbarStore((state)=>state.isvisible)
  const isSideBarOpen = useNavbarStore((state)=>state.isSideBarOpen)
  const setField = useNavbarStore((state)=>state.setField)
  const userData = store.getItem('user')?.data ?? '';
  const setCustomAction = useBackHandlerStore((state)=>state.setCustomAction)

  useEffect(()=>{
    // 뒤로가기 대신 이 액션을 실행함!
    if(isInfo){
      setCustomAction(()=>{
            setIsInfo(false);
            })
        }
    else{
      setCustomAction('')
    }
  },[isInfo])

  const toggleSideMenu = () => {
    setField('isSideBarOpen',!isSideBarOpen); // 사이드 메뉴 바의 열림/닫힘 상태를 토글합니다.
  };

  const getLogoContent = () => {
    switch (location.pathname) {
      case '/home':
        return <LogoImg src={logo} alt="Contact Logo" style={{scale:0.8}}/>;
      case '/recruitment':
        return <LogoText>캘린더</LogoText>;
      case '/searching-promotion-school':
        return <LogoContainer>
        <LogoText>홍보 대상 탐색</LogoText>
        <Icon_Inform_White onClick={()=>setIsInfo((state)=>!state)}/>
        </LogoContainer>;
      case '/promotion-result-list':
        return <LogoText>홍보 결과 목록</LogoText>;
      case '/promotion-result-add':
        return <LogoText>홍보 결과 기록</LogoText>;
      case '/interested-person-management':
        return <LogoText>관심표명자 관리</LogoText>;
      case '/my-info':
        return <LogoText>내 정보</LogoText>;
      case '/todays-promotion':
        return <LogoContainer>
          <LogoText>오늘의 홍보 학교</LogoText>
          <AIRecommendationWrapper>AI 추천</AIRecommendationWrapper>
          </LogoContainer>;
      default:
        return null;
    }
  };

  const logout = async()=>{
    await postLogout();
    localStorage.clear(); // ✅ 모든 로컬스토리지 데이터 삭제
    sessionStorage.clear(); // ✅ 모든 세션스토리지 데이터 삭제
    navigate('/'); // 로그인 페이지로 이동
  }

  return (
    <>
    {isvisible && 
    <>
      <Info isInfo={isInfo} setIsInfo={setIsInfo}/>
      <NavBarContainer $isHome={location.pathname === '/home'}>
        {location.pathname !== '/home' &&           
          <div onClick={()=>navigate('/home')} style={{display:'flex',justifyContent:'center', alignItems:'center', position:'absolute'}}>
            <Icon_ArrowBack />
          </div>
        }
        <NavBarLogo>
          {getLogoContent()} {/* 현재 경로에 따라 로고 이미지를 표시합니다. */}
        </NavBarLogo>
        <NavItem icon={hamburger} onClick={toggleSideMenu} /> {/* 햄버거 아이콘 클릭 시 사이드 메뉴 바를 토글합니다. */}
      </NavBarContainer>
      
      <Overlay $isopen={isSideBarOpen} onClick={toggleSideMenu} /> {/* 오버레이 클릭 시 사이드 메뉴 바를 닫습니다. */}
      <SideMenu $isopen={isSideBarOpen}>
        <SideMenuHeader>
          <IconButton onClick={toggleSideMenu}>
            <CloseIcon />
          </IconButton>
        </SideMenuHeader>
        <UserInfo>
          <div>
            <div className="greeting">안녕하세요</div>
            <div className="username">{userData.user_name}님</div>
          </div>
          <LogoutBox onClick={logout}>
            로그아웃
          </LogoutBox>
        </UserInfo>
        <StyledDivider />
        <ul>
          <li><Link to="/recruitment" onClick={toggleSideMenu}>모집 일정</Link></li>
          <li><Link to="/searching-promotion-school" onClick={toggleSideMenu}>홍보 대상 탐색</Link></li>
          <li><Link to="/promotion-result-list" onClick={toggleSideMenu}>홍보 결과 목록</Link></li>
          <li><Link to="/interested-person-management" onClick={toggleSideMenu}>관심표명자 관리</Link></li>
          <li><Link to="/my-info" onClick={toggleSideMenu}>내 정보</Link></li>
        </ul>
      </SideMenu>
      </>
      }
    </>
  );
};

export default NavBar; // NavBar 컴포넌트를 export합니다.